import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "@/components/ui/sonner";
import {
  cleanupDisparos,
  controlDisparoCampaign,
  createAdvancedDisparo,
  createSimpleDisparo,
  listDisparoCampaignMessages,
  listDisparoCampaigns,
  rescheduleDisparoCampaign,
} from "@/lib/disparos-api";
import type {
  CreateAdvancedDisparoPayload,
  CreateSimpleDisparoPayload,
  DisparoCampaign,
  DisparoCampaignMessage,
  DisparoCleanupOptions,
  DisparoMessageFilters,
} from "@/lib/disparos-types";

const QUERY_KEY = "disparo-campaigns";
const MESSAGES_QUERY_KEY = "disparo-campaign-messages";

/**
 * Estratégia de cache (disparos):
 * - Campanhas: staleTime 60s, refetch 60s só com aba visível.
 * - Mensagens: staleTime/gcTime 5min, sessionStorage TTL 5min entre remounts, filtros client-side.
 * - DB-first: lê `disparo_messages`; fallback silencioso ao provedor só se tabela vazia.
 */
const CAMPAIGNS_STALE_MS = 60_000;
const CAMPAIGNS_GC_MS = 5 * 60_000;
const CAMPAIGNS_REFETCH_MS = 60_000;
const MESSAGES_STALE_MS = 5 * 60_000;
const MESSAGES_GC_MS = 10 * 60_000;
const MESSAGES_SESSION_TTL_MS = 5 * 60_000;

type MessagesCachePayload = { messages: DisparoCampaignMessage[]; total: number };

function messagesSessionKey(workspaceId: string, campaignId: string) {
  return `disparo-messages:${workspaceId}:${campaignId}`;
}

function readMessagesSessionCache(
  workspaceId: string,
  campaignId: string,
): { data: MessagesCachePayload; updatedAt: number } | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(messagesSessionKey(workspaceId, campaignId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; data: MessagesCachePayload };
    if (Date.now() - parsed.at > MESSAGES_SESSION_TTL_MS) {
      sessionStorage.removeItem(messagesSessionKey(workspaceId, campaignId));
      return null;
    }
    return { data: parsed.data, updatedAt: parsed.at };
  } catch {
    return null;
  }
}

function clearMessagesSessionCache(workspaceId: string, campaignId: string) {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(messagesSessionKey(workspaceId, campaignId));
}

function writeMessagesSessionCache(
  workspaceId: string,
  campaignId: string,
  data: MessagesCachePayload,
) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      messagesSessionKey(workspaceId, campaignId),
      JSON.stringify({ at: Date.now(), data }),
    );
  } catch {
    // quota exceeded — ignora
  }
}

export function useDisparos(
  workspaceId: string | null,
  options?: { enabled?: boolean },
) {
  const queryClient = useQueryClient();
  const queryEnabled = (options?.enabled ?? true) && !!workspaceId;

  const campaignsQuery = useQuery({
    queryKey: [QUERY_KEY, workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [] as DisparoCampaign[];
      const { data, error } = await listDisparoCampaigns(workspaceId, { syncProvider: true });
      if (error) throw new Error(error);
      return data?.campaigns ?? [];
    },
    enabled: queryEnabled,
    staleTime: CAMPAIGNS_STALE_MS,
    gcTime: CAMPAIGNS_GC_MS,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchInterval: () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return false;
      }
      return CAMPAIGNS_REFETCH_MS;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, workspaceId] });
  }, [queryClient, workspaceId]);

  const createSimple = useMutation({
    mutationFn: async (payload: CreateSimpleDisparoPayload) => {
      const { data, error } = await createSimpleDisparo(payload);
      if (error) throw new Error(error);
      return data;
    },
    onSuccess: () => {
      toast.success("Disparo criado com sucesso");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createAdvanced = useMutation({
    mutationFn: async (payload: CreateAdvancedDisparoPayload) => {
      const { data, error } = await createAdvancedDisparo(payload);
      if (error) throw new Error(error);
      return data;
    },
    onSuccess: () => {
      toast.success("Disparo avançado criado com sucesso");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const controlCampaign = useMutation({
    mutationFn: async ({
      campaignId,
      action,
    }: {
      campaignId: string;
      action: "pause" | "resume" | "delete";
    }) => {
      if (!workspaceId) throw new Error("Workspace não encontrado");
      const { error } = await controlDisparoCampaign(workspaceId, campaignId, action);
      if (error) throw new Error(error);
    },
    onSuccess: (_, { action }) => {
      const labels = { pause: "pausada", resume: "retomada", delete: "excluída" };
      toast.success(`Campanha ${labels[action]}`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reschedule = useMutation({
    mutationFn: async ({
      campaignId,
      integrationId,
      scheduledAt,
    }: {
      campaignId: string;
      integrationId: string;
      scheduledAt: string;
    }) => {
      if (!workspaceId) throw new Error("Workspace não encontrado");
      const { data, error } = await rescheduleDisparoCampaign(workspaceId, campaignId, {
        integration_id: integrationId,
        scheduled_at: scheduledAt,
      });
      if (error) throw new Error(error);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Disparo reagendado para ${data?.recipient_count ?? 0} contato(s) pendente(s)`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cleanup = useMutation({
    mutationFn: async (options: DisparoCleanupOptions) => {
      if (!workspaceId) throw new Error("Workspace não encontrado");
      const { data, error } = await cleanupDisparos(workspaceId, options);
      if (error) throw new Error(error);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`${data?.deleted ?? 0} registro(s) removido(s)`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return {
    campaigns: campaignsQuery.data ?? [],
    loading: campaignsQuery.isLoading,
    error: campaignsQuery.error,
    refetch: campaignsQuery.refetch,
    createSimple,
    createAdvanced,
    controlCampaign,
    reschedule,
    cleanup,
  };
}

function filterCampaignMessages(
  messages: DisparoCampaignMessage[],
  filters?: DisparoMessageFilters,
): DisparoCampaignMessage[] {
  let filtered = messages;

  if (filters?.status && filters.status !== "all") {
    filtered = filtered.filter((message) => message.status === filters.status);
  }

  if (filters?.search?.trim()) {
    const query = filters.search.trim().toLowerCase();
    filtered = filtered.filter(
      (message) =>
        message.phone.includes(query) ||
        (message.lead_name?.toLowerCase().includes(query) ?? false),
    );
  }

  return filtered;
}

export function useDisparoCampaignMessages(
  workspaceId: string | null,
  campaignId: string | null,
  filters?: DisparoMessageFilters,
) {
  const messagesQuery = useQuery({
    queryKey: [MESSAGES_QUERY_KEY, workspaceId, campaignId],
    queryFn: async (): Promise<MessagesCachePayload> => {
      if (!workspaceId || !campaignId) {
        return { messages: [], total: 0 };
      }

      const { data, error } = await listDisparoCampaignMessages(workspaceId, campaignId);
      if (error) throw new Error(error);

      const payload: MessagesCachePayload = {
        messages: data?.messages ?? [],
        total: data?.total ?? 0,
      };
      writeMessagesSessionCache(workspaceId, campaignId, payload);
      return payload;
    },
    enabled: !!workspaceId && !!campaignId,
    staleTime: MESSAGES_STALE_MS,
    gcTime: MESSAGES_GC_MS,
    refetchOnWindowFocus: false,
    initialData: () => {
      if (!workspaceId || !campaignId) return undefined;
      return readMessagesSessionCache(workspaceId, campaignId)?.data;
    },
    initialDataUpdatedAt: () => {
      if (!workspaceId || !campaignId) return undefined;
      return readMessagesSessionCache(workspaceId, campaignId)?.updatedAt;
    },
  });

  useEffect(() => {
    if (messagesQuery.error) {
      const msg =
        messagesQuery.error instanceof Error
          ? messagesQuery.error.message
          : "Erro ao carregar mensagens";
      toast.error(msg);
    }
  }, [messagesQuery.error]);

  const filteredMessages = useMemo(
    () => filterCampaignMessages(messagesQuery.data?.messages ?? [], filters),
    [messagesQuery.data?.messages, filters?.status, filters?.search],
  );

  const total = useMemo(() => {
    const hasClientFilters =
      (filters?.status && filters.status !== "all") || Boolean(filters?.search?.trim());
    return hasClientFilters ? filteredMessages.length : (messagesQuery.data?.total ?? 0);
  }, [filteredMessages.length, filters?.status, filters?.search, messagesQuery.data?.total]);

  return {
    messages: filteredMessages,
    total,
    loading: messagesQuery.isLoading,
    refetch: () => {
      if (workspaceId && campaignId) clearMessagesSessionCache(workspaceId, campaignId);
      return messagesQuery.refetch();
    },
  };
}
